// 金融機関の明細CSVをZeniの取引インポート形式へ変換する際の共通処理。
//
// 楽天カード（利用明細）とドコモSMTBネット銀行（入出金明細）はいずれも
// Shift-JIS 主体のCSVで、日付・金額・摘要という同じ構造を持つため、
// デコード・行分割・日付/金額の正規化・カテゴリ推定をここに集約する。

// 利用可能なカテゴリ。存在しないカテゴリIDを取引に付けるとサーバー側の
// 外部キー制約で失敗するため、必ずこの一覧の中から選ぶ。
export interface CsvCategoryRef {
  id: string
  type: string // 'income' | 'expense' | 'transfer'
}

// POST /transactions/import に渡す1行。
export interface CsvImportRow {
  date: string
  // transfer は口座間の資産移動（iDeCo の拠出金など）。収支には計上されない
  type: 'income' | 'expense' | 'transfer'
  category: string
  description: string
  amount: number
  accountId: string
  // 振替先口座。type が 'transfer' のときだけ意味を持つ
  toAccountId?: string
  currency: string
  note?: string
}

export interface CsvParseResult {
  rows: CsvImportRow[]
  parsed: number   // データ行として解釈できた件数
  ignored: number  // 金額0・日付不正などで無視した件数
}

// 摘要キーワード → デフォルトカテゴリID。上から順に最初に一致したものを採用する。
// 店名ベースの一般的な対応表で、金融機関固有の摘要（「口座振替」「ＡＴＭ」等）は
// 各パーサー側で先に判定してからここへフォールバックする。
export const MERCHANT_CATEGORY: ReadonlyArray<readonly [string, string]> = [
  // スーパー・コンビニ → 食費・日用品
  ['イオン', 'food'], ['ライフ', 'food'], ['マルエツ', 'food'], ['業務スーパー', 'food'],
  ['西友', 'food'], ['ベルク', 'food'], ['ヤオコー', 'food'], ['サミット', 'food'],
  ['オーケー', 'food'], ['コープ', 'food'], ['まいばすけっと', 'food'], ['成城石井', 'food'],
  ['セブン', 'food'], ['ローソン', 'food'], ['ファミリーマート', 'food'], ['ファミマ', 'food'],
  ['ミニストップ', 'food'], ['デイリーヤマザキ', 'food'],
  // 飲食店 → 外食
  ['マクドナルド', 'dining'], ['スターバックス', 'dining'], ['スタバ', 'dining'], ['ドトール', 'dining'],
  ['コメダ', 'dining'], ['タリーズ', 'dining'], ['サンマルク', 'dining'], ['すき家', 'dining'],
  ['吉野家', 'dining'], ['松屋', 'dining'], ['なか卯', 'dining'], ['丸亀', 'dining'],
  ['サイゼリヤ', 'dining'], ['ガスト', 'dining'], ['デニーズ', 'dining'], ['バーミヤン', 'dining'],
  ['ジョナサン', 'dining'], ['はま寿司', 'dining'], ['くら寿司', 'dining'], ['スシロー', 'dining'],
  ['ケンタッキー', 'dining'], ['モスバーガー', 'dining'], ['一風堂', 'dining'], ['鳥貴族', 'dining'],
  ['ワタミ', 'dining'], ['居酒屋', 'dining'], ['焼肉', 'dining'], ['レストラン', 'dining'],
  ['カフェ', 'dining'],
  // 交通
  ['ＪＲ', 'transport'], ['JR', 'transport'], ['旅客鉄道', 'transport'], ['メトロ', 'transport'],
  ['ＳＵＩＣＡ', 'transport'], ['SUICA', 'transport'], ['suica', 'transport'], ['ＰＡＳＭＯ', 'transport'],
  ['PASMO', 'transport'], ['タクシー', 'transport'], ['ＥＴＣ', 'transport'], ['ETC', 'transport'],
  ['高速道路', 'transport'], ['鉄道', 'transport'], ['航空', 'transport'], ['ＡＮＡ', 'transport'],
  ['ANA', 'transport'], ['ＪＡＬ', 'transport'], ['JAL', 'transport'],
  // 光熱費
  ['電力', 'utilities'], ['でんき', 'utilities'], ['ガス', 'utilities'], ['水道', 'utilities'],
  // 通信費
  ['ドコモ', 'telecom'], ['ｄｏｃｏｍｏ', 'telecom'], ['docomo', 'telecom'], ['ソフトバンク', 'telecom'],
  ['ＳｏｆｔＢａｎｋ', 'telecom'], ['SoftBank', 'telecom'], ['楽天モバイル', 'telecom'], ['ＯＣＮ', 'telecom'],
  ['OCN', 'telecom'], ['ＮＴＴ', 'telecom'], ['NTT', 'telecom'], ['Ｙ！ｍｏｂｉｌｅ', 'telecom'],
  ['ＵＱ', 'telecom'], ['プロバイダ', 'telecom'], ['インターネット', 'telecom'],
  // サブスク（月額の定額サービス）
  ['Ｎｅｔｆｌｉｘ', 'subscription'], ['Netflix', 'subscription'], ['Ｓｐｏｔｉｆｙ', 'subscription'],
  ['Spotify', 'subscription'], ['ＹｏｕＴｕｂｅ', 'subscription'], ['YouTube', 'subscription'],
  ['Ｈｕｌｕ', 'subscription'], ['Hulu', 'subscription'], ['Ｄｉｓｎｅｙ', 'subscription'],
  ['Disney', 'subscription'], ['Ａｍａｚｏｎプライム', 'subscription'], ['ＤＡＺＮ', 'subscription'],
  // 娯楽費（都度課金）
  ['Ｓｔｅａｍ', 'entertainment'], ['Steam', 'entertainment'],
  ['Ｎｉｎｔｅｎｄｏ', 'entertainment'], ['Nintendo', 'entertainment'], ['ＰｌａｙＳｔａｔｉｏｎ', 'entertainment'],
  ['映画', 'entertainment'], ['シネマ', 'entertainment'], ['カラオケ', 'entertainment'], ['ゲーム', 'entertainment'],
  // 医療費
  ['クリニック', 'healthcare'], ['病院', 'healthcare'], ['医院', 'healthcare'], ['歯科', 'healthcare'],
  ['薬局', 'healthcare'], ['ドラッグ', 'healthcare'], ['マツモトキヨシ', 'healthcare'], ['ウエルシア', 'healthcare'],
  ['ツルハ', 'healthcare'], ['スギ薬局', 'healthcare'], ['サンドラッグ', 'healthcare'], ['調剤', 'healthcare'],
  // 衣服費
  ['ユニクロ', 'clothing'], ['ＵＮＩＱＬＯ', 'clothing'], ['UNIQLO', 'clothing'], ['ＧＵ', 'clothing'],
  ['ＺＡＲＡ', 'clothing'], ['ZARA', 'clothing'], ['Ｈ＆Ｍ', 'clothing'], ['無印良品', 'clothing'],
  ['ＺＯＺＯ', 'clothing'], ['ZOZO', 'clothing'], ['ビームス', 'clothing'], ['ナノユニバース', 'clothing'],
  // 日用品（食費・日用品）と家具・家電
  ['ダイソー', 'food'], ['セリア', 'food'], ['カインズ', 'food'], ['コーナン', 'food'],
  ['ホームセンター', 'food'],
  ['ニトリ', 'furniture'], ['ＩＫＥＡ', 'furniture'], ['ビックカメラ', 'furniture'],
  ['ヨドバシ', 'furniture'], ['ヤマダ電機', 'furniture'], ['ケーズデンキ', 'furniture'],
  ['エディオン', 'furniture'],
  // 教育費
  ['書店', 'education'], ['書房', 'education'], ['ブックオフ', 'education'], ['Ｕｄｅｍｙ', 'education'],
  ['Udemy', 'education'], ['スクール', 'education'], ['予備校', 'education'],
  // 住居費
  ['家賃', 'housing'], ['管理費', 'housing'], ['賃貸', 'housing'], ['不動産', 'housing'],
  ['住宅ローン', 'housing'],
  // 車両費
  ['ガソリン', 'car'], ['ＥＮＥＯＳ', 'car'], ['エネオス', 'car'], ['出光', 'car'],
  ['コスモ石油', 'car'], ['車検', 'car'], ['オートバックス', 'car'], ['駐車場', 'car'],
  // 美容費
  ['美容室', 'beauty'], ['ヘアサロン', 'beauty'], ['美容院', 'beauty'], ['理容', 'beauty'],
  // 旅行費
  ['楽天トラベル', 'travel'], ['じゃらん', 'travel'], ['ホテル', 'travel'], ['旅館', 'travel'],
  ['ＪＴＢ', 'travel'], ['エクスペディア', 'travel'],
  // ペット費
  ['ペット', 'pet'], ['動物病院', 'pet'],
  // 寄付・ふるさと納税
  ['ふるさと納税', 'donation'], ['ふるさとチョイス', 'donation'], ['寄付', 'donation'],
  // 手数料
  ['手数料', 'fees'],
]

// 明細の摘要は半角カナ・全角英数が混在する（例: ﾏﾂﾓﾄｷﾖｼ, ＡＭＡＺＯＮ）。
// NFKC正規化＋大文字化してからキーワードと突き合わせる。
export function normalizeForMatch(value: string): string {
  return value.normalize('NFKC').toUpperCase()
}

// キーワード表を順に見て、最初に一致しかつ利用可能なカテゴリIDを返す。無ければ null。
export function matchKeywordCategory(
  description: string,
  table: ReadonlyArray<readonly [string, string]>,
  available: ReadonlySet<string>,
): string | null {
  const target = normalizeForMatch(description)
  for (const [keyword, categoryId] of table) {
    if (target.includes(normalizeForMatch(keyword)) && available.has(categoryId)) return categoryId
  }
  return null
}

// 優先順のIDのうち利用可能な最初のものを返す。無ければ利用可能な先頭を返す。
export function pickCategory(preferred: readonly string[], primary: string[], secondary: string[]): string | null {
  const all = new Set([...primary, ...secondary])
  for (const id of preferred) {
    if (all.has(id)) return id
  }
  return primary[0] ?? secondary[0] ?? null
}

// 収入・支出それぞれのフォールバック先カテゴリを決める。
// 存在しないIDを送るとサーバーが外部キー制約で失敗するため、実在するカテゴリから選ぶ。
export function resolveFallbackCategories(categories: ReadonlyArray<CsvCategoryRef>): {
  expenseIds: string[]
  incomeIds: string[]
  fallbackExpense: string
  fallbackIncome: string
} {
  const expenseIds = categories.filter((c) => c.type === 'expense').map((c) => c.id)
  const incomeIds = categories.filter((c) => c.type === 'income').map((c) => c.id)

  const fallbackExpense = pickCategory(['other', 'food', 'dining'], expenseIds, incomeIds)
  const fallbackIncome = pickCategory(['income_other', 'salary', 'side_income'], incomeIds, expenseIds)
  if (!fallbackExpense || !fallbackIncome) {
    throw new Error('利用可能なカテゴリがありません。カテゴリ管理でカテゴリを作成してから再度お試しください。')
  }
  return { expenseIds, incomeIds, fallbackExpense, fallbackIncome }
}

// 金融機関のCSVはShift-JISだが、Excel等で開いて保存し直すとUTF-8(BOM付き)になる。
// BOMの有無とUTF-8として妥当かで判定し、駄目ならShift-JISとして読む。
export function decodeCsvBuffer(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes)
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('shift-jis').decode(bytes)
  }
}

// 引用符を考慮してCSVの1行をフィールドに分割する。
export function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else { inQuotes = false }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      fields.push(cur); cur = ''
    } else {
      cur += ch
    }
  }
  fields.push(cur)
  return fields.map((f) => f.trim())
}

// BOM除去・改行正規化のうえ、空行を除いた行の配列を返す。
export function splitCsvLines(text: string): string[] {
  return text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim() !== '')
}

// "YYYY/MM/DD" や "YYYY/M/D" を "YYYY-MM-DD" に正規化する。
// GMOあおぞらネット銀行のように区切り無しの "YYYYMMDD" も受け付ける。
// 解釈できなければ null。
export function normalizeDate(raw: string): string | null {
  const value = raw.trim()
  const m = value.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
  if (m) {
    const [, y, mo, d] = m
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  // 区切り無しの8桁は月日の妥当性まで確認する。口座番号や整理番号のような
  // 数字だけの列を日付として誤って取り込まないため。
  const compact = value.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (!compact) return null
  const [, y, mo, d] = compact
  if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31) return null
  return `${y}-${mo}-${d}`
}

// "1,234" "￥1,234" "1,234円" などを数値へ。空欄や解釈不能なら NaN。
export function parseAmount(raw: string): number {
  return parseFloat(raw.replace(/[",\s¥￥円]/g, ''))
}
